import { ConnectError } from "@connectrpc/connect";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { CampaignLottery_Result } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import { findLocalizedErrorMessage } from "../../../libs/connect";
import { AppError, ERROR_CODES } from "../../../libs/errors";
import { t } from "../../../libs/i18n";

// GetMyCampaignStatuses は fund source を持たないテナントにも後方互換でゼロ値を返すため、
// id の有無だけでは「そのテナントに無いキャンペーン」を弾けない
const NIL_UUID = "00000000-0000-0000-0000-000000000000";

type ApplySearch = {
  campaign?:
    | "r7OnSignup"
    | "r8AntiInflation"
    | "r8OnSignup"
    | "r8MiyagiResidenceCard";
};

export const Route = createFileRoute("/_authed/lottery/apply")({
  validateSearch: (search: Record<string, unknown>): ApplySearch => {
    return {
      campaign: (search.campaign as ApplySearch["campaign"]) ?? "r7OnSignup",
    };
  },
  loader: async ({ context: { tenant, client }, location }) => {
    const searchParams = location.search as ApplySearch;
    const campaignType = searchParams.campaign ?? "r7OnSignup";

    if (!["miyagi", "marumori", "shiga-otsu"].includes(tenant.slug)) {
      return redirect({
        to: "/",
        state: {
          toast: { message: t("キャンペーンが見つかりません"), type: "error" },
        },
      });
    }

    const campaignStatuses = await client
      .getMyCampaignStatuses({})
      .catch((error) => {
        throw new AppError(
          ERROR_CODES.CONNECT_GET_MY_CAMPAIGN_STATUSES_FAILED,
          error.message,
          {
            originalError: error,
          },
        );
      });

    // キャンペーンタイプに応じてfundSourceIdを取得
    const fundSourceId =
      campaignType === "r8AntiInflation"
        ? campaignStatuses.r8AntiInflation?.fundSource?.id
        : campaignType === "r8OnSignup"
          ? campaignStatuses.r8OnSignup?.fundSource?.id
          : campaignType === "r8MiyagiResidenceCard"
            ? campaignStatuses.r8MiyagiResidenceCard?.fundSource?.id
            : campaignStatuses.r7OnSignup?.fundSource?.id;

    if (!fundSourceId || fundSourceId === NIL_UUID) {
      return redirect({
        to: "/",
        state: {
          toast: { message: t("キャンペーンが見つかりません"), type: "error" },
        },
      });
    }

    const response = await client
      .applyCampaign({
        fundSourceId,
      })
      .catch((error) => {
        // errorhandler は message をサニタイズするので、包んでしまうと画面に出るのは
        // 「[failed_precondition] failed_precondition」になる。hint が付いた拒否は
        // 想定内の状態なので、理由をトーストにしてホームへ返す
        const hint =
          error instanceof ConnectError
            ? findLocalizedErrorMessage(error)
            : undefined;
        if (hint) {
          throw redirect({
            to: "/",
            state: { toast: { message: hint, type: "error" } },
          });
        }
        throw new AppError(
          ERROR_CODES.CONNECT_APPLY_CAMPAIGN_FAILED,
          error.message,
          {
            originalError: error,
          },
        );
      });

    if (!response.campaignLottery) {
      return redirect({
        to: "/",
        state: {
          toast: {
            message: t("抽選キャンペーンが見つかりません"),
            type: "error",
          },
        },
      });
    }

    if (
      response.campaignLottery.result === CampaignLottery_Result.UNSPECIFIED
    ) {
      throw new AppError(ERROR_CODES.LOTTERY_RESULT_UNSPECIFIED);
    }

    // 丸森は外れあり抽選
    if (tenant.slug === "marumori") {
      return redirect({
        to: "/lottery/result",
        search: {
          result: response.campaignLottery.result,
          wonBalance: response.campaignLottery.wonBalance
            ? Number(response.campaignLottery.wonBalance)
            : 0,
        },
        replace: true,
      });
    }

    // みやぎ・大津市は外れなし抽選（配布）
    return redirect({
      to: "/lottery/grant/result",
      search: {
        wonBalance: response.campaignLottery.wonBalance
          ? Number(response.campaignLottery.wonBalance)
          : 0,
      },
      replace: true,
    });
  },
});
